import type { GraphQLError, GraphQLErrorExtensions } from "graphql";
import { FieldValues, Path } from "react-hook-form";
import {
  EmptyPrefixedFieldValues,
  FieldErrorMatcher,
  makeFieldErrorMatcher,
  FieldsGetterErrorMatcher,
  FieldsGetterReturn,
  makeGraphQLErrorProcessor,
} from "components/Shared/Forms/graphQLErrorProcessing";

type ErrorMessagesByFieldName = { [fieldName: string]: string[] };

type ValidationErrorExtensions = {
  fieldValidationErrors: ErrorMessagesByFieldName;
};

type ServerGraphQLErrorExtensions = ValidationErrorExtensions;

export function isServerGraphQLValidationErrorExtensions(
  extensions: unknown,
): extensions is ValidationErrorExtensions {
  if (!extensions || typeof extensions !== "object") return false;
  return "fieldValidationErrors" in extensions;
}

export function isServerGraphQLErrorExtensions(
  extensions: unknown,
): extensions is ServerGraphQLErrorExtensions {
  if (!extensions || typeof extensions !== "object") return false;
  return isServerGraphQLValidationErrorExtensions(extensions);
}

type ServerGraphQLErrorMatcherExtensions = { field: string };

type ServerGraphQLFieldsGetterErrorMatcher<TFieldValues extends FieldValues> =
  FieldsGetterErrorMatcher<ServerGraphQLErrorMatcherExtensions, TFieldValues>;

type ServerGraphQLFieldErrorMatcher<TFieldValues extends FieldValues> =
  FieldErrorMatcher<ServerGraphQLErrorMatcherExtensions, TFieldValues>;

function getIsExtensionsMatch(
  extensions: GraphQLErrorExtensions,
  extensionsToMatch: Partial<ServerGraphQLErrorMatcherExtensions>,
) {
  if (!isServerGraphQLErrorExtensions(extensions)) return false;

  if (isServerGraphQLValidationErrorExtensions(extensions)) {
    if (
      extensionsToMatch.field &&
      !extensions.fieldValidationErrors?.[extensionsToMatch.field]
    ) {
      return false;
    }

    return true;
  }

  return true;
}

export function makeServerGraphQLErrorMatcher<TFieldValues extends FieldValues>(
  matchers: ServerGraphQLFieldErrorMatcher<TFieldValues>[],
) {
  return makeFieldErrorMatcher<
    ServerGraphQLErrorMatcherExtensions,
    TFieldValues
  >(matchers, { getIsExtensionsMatch });
}

export function makeServerGraphQLErrorProcessor<
  TFieldValues extends FieldValues,
>(matchers: ServerGraphQLFieldErrorMatcher<TFieldValues>[]) {
  return makeGraphQLErrorProcessor({
    getFieldErrorsForError: makeServerGraphQLErrorMatcher(matchers),
  });
}

type GetServerGraphQLErrorMatchersForFieldsOptions<P extends string> = {
  fieldNamePrefix?: P;
};

export function getServerGraphQLErrorMatchersForFields<
  F extends string,
  P extends string,
>(
  fields: readonly F[],
  { fieldNamePrefix }: GetServerGraphQLErrorMatchersForFieldsOptions<P> = {},
) {
  return fields.map<
    ServerGraphQLFieldsGetterErrorMatcher<EmptyPrefixedFieldValues<F, P>>
  >((field) => {
    const extensions: Partial<ServerGraphQLErrorMatcherExtensions> = { field };

    const getFieldErrors = (error: GraphQLError) => {
      if (
        !error.extensions ||
        !isServerGraphQLValidationErrorExtensions(error.extensions)
      ) {
        return;
      }
      const messages = error.extensions.fieldValidationErrors?.[field];

      const fieldName = `${
        fieldNamePrefix ? `${fieldNamePrefix}.` : ""
      }${field}` as Path<EmptyPrefixedFieldValues<F, P>>;

      return { [fieldName]: messages } as FieldsGetterReturn<
        EmptyPrefixedFieldValues<F, P>
      >;
    };

    return {
      getFieldErrors,
      matcher: { extensions },
    };
  });
}
